export function calculateActualPpcm(
  baseLength: number,
  newLength: number,
  basePpcm: number
): number {
  const sizeDiff = newLength / baseLength
  return basePpcm * sizeDiff
}

export function calculateSize(length: number, ppcm: number): number {
  return Math.round((length / ppcm) * 10)
}

/**
 * Checks if logo dimensions fit within the transfer method maximum size.
 * Considers both normal and rotated orientations.
 * @param width Logo width in mm
 * @param height Logo height in mm
 * @param maxSize Maximum size string in format "WIDTHxHEIGHT" (e.g., "320x380")
 * @returns true if the logo fits within the maximum dimensions
 */
export function doesLogoFitInTransferMethod(
  width: number,
  height: number,
  maxSize: string
): boolean {
  if (!maxSize || !maxSize.includes('x')) {
    return true; // If no size restriction, allow any size
  }

  const [maxWidth, maxHeight] = maxSize.split('x').map(Number);
  
  // Check if dimensions are valid
  if (isNaN(maxWidth) || isNaN(maxHeight) || maxWidth <= 0 || maxHeight <= 0) {
    return true; // If invalid max size, allow any size
  }

  // Check normal orientation
  const fitsNormal = width <= maxWidth && height <= maxHeight;
  
  // Check rotated orientation (90 degrees)
  const fitsRotated = width <= maxHeight && height <= maxWidth;
  
  return fitsNormal || fitsRotated;
}

/**
 * Corrects logo dimensions to fit within the transfer method maximum size.
 * Maintains aspect ratio and considers both orientations.
 * @param width Current logo width in mm
 * @param height Current logo height in mm
 * @param maxSize Maximum size string in format "WIDTHxHEIGHT" (e.g., "320x380")
 * @returns Corrected dimensions {width, height}
 */
export function correctLogoSizeForTransferMethod(
  width: number,
  height: number,
  maxSize: string
): { width: number; height: number } {
  if (!maxSize || !maxSize.includes('x')) {
    return { width, height }; // If no size restriction, return original
  }

  const [maxWidth, maxHeight] = maxSize.split('x').map(Number);
  
  // Check if dimensions are valid
  if (isNaN(maxWidth) || isNaN(maxHeight) || maxWidth <= 0 || maxHeight <= 0) {
    return { width, height }; // If invalid max size, return original
  }

  // If already fits, return original dimensions
  if (doesLogoFitInTransferMethod(width, height, maxSize)) {
    return { width, height };
  }

  const aspectRatio = width / height;

  // Calculate scale factors for both orientations
  const scaleNormal = Math.min(maxWidth / width, maxHeight / height);
  const scaleRotated = Math.min(maxHeight / width, maxWidth / height);

  // Choose the orientation that allows for the largest size
  if (scaleNormal >= scaleRotated) {
    // Use normal orientation
    return {
      width: Math.round(width * scaleNormal * 100) / 100,
      height: Math.round(height * scaleNormal * 100) / 100
    };
  } else {
    // Use rotated orientation - but keep the same aspect ratio
    return {
      width: Math.round(width * scaleRotated * 100) / 100,
      height: Math.round(height * scaleRotated * 100) / 100
    };
  }
}

/**
 * The two sides of a method size "AxB" as the API's policy reads a limit:
 * S = the smaller number, L = the larger one (either order is the same
 * limit). null = no usable limit (empty, no "x", not a number, a side <= 0) -
 * the same inputs doesLogoFitInTransferMethod treats as "no restriction".
 */
function parseMethodSizeLimit(
  maxSize: string
): { shortMm: number; longMm: number } | null {
  if (!maxSize || !maxSize.includes('x')) {
    return null;
  }
  const [first, second] = maxSize.split('x').map(Number);
  if (isNaN(first) || isNaN(second) || first <= 0 || second <= 0) {
    return null;
  }
  const a = Math.floor(first);
  const b = Math.floor(second);
  if (a < 1 || b < 1) {
    return null;
  }
  return { shortMm: Math.min(a, b), longMm: Math.max(a, b) };
}

// numerator / denominator rounded to a whole number, half AWAY from zero
// (both are positive whole numbers here) - the API's
// Math.Round(decimal, MidpointRounding.AwayFromZero). Whole-number
// arithmetic only, so an exact .5 is decided exactly.
function divideRoundHalfUp(numerator: number, denominator: number): number {
  return Math.floor((2 * numerator + denominator) / (2 * denominator));
}

/**
 * Fits logo dimensions within the transfer method maximum size in WHOLE mm -
 * EXACTLY the API's LogoMethodMaxSizePolicy.Clamp, so what the form shows
 * after a method change is the size the server stores (the FE has no limits
 * of its own; the number comes from the server's `size`).
 *
 * The rule (orientation-independent): a limit "AxB" has S = min(A,B) and
 * L = max(A,B); a logo fits when its shortest side <= S and its longest side
 * <= L (300x400 allows 400x300 and 300x400, never 350x350). A logo that does
 * not fit is scaled down proportionally, the orientation kept:
 *  - S * longest <= L * shortest: the short side binds - shortest = S,
 *    longest = round(longest * S / shortest);
 *  - otherwise the long side binds - longest = L,
 *    shortest = round(shortest * L / longest);
 * ONE rounding, half away from zero, never below 1 mm, never above the
 * limit. (correctLogoSizeForTransferMethod rounds to 2 decimals first; a
 * second rounding on top of that gave 1 mm more than the API for sizes
 * landing on x.495-x.499.)
 *
 * Like the API's resize the size is taken in whole mm (the integer part of
 * each side). Fail-open like the helpers above: an empty or invalid maxSize
 * (= no limit) or a side <= 0 returns the input untouched.
 * @param width Current logo width in mm
 * @param height Current logo height in mm
 * @param maxSize Maximum size string in format "WIDTHxHEIGHT" (e.g., "300x400")
 * @returns Fitted dimensions {width, height}
 */
export function fitSizeToTransferMethod(
  width: number,
  height: number,
  maxSize: string
): { width: number; height: number } {
  const limit = parseMethodSizeLimit(maxSize);
  if (!limit) {
    return { width, height };
  }

  const wholeWidth = Math.trunc(width);
  const wholeHeight = Math.trunc(height);
  if (!(wholeWidth > 0) || !(wholeHeight > 0)) {
    return { width, height };
  }

  const shortest = Math.min(wholeWidth, wholeHeight);
  const longest = Math.max(wholeWidth, wholeHeight);
  if (shortest <= limit.shortMm && longest <= limit.longMm) {
    return { width: wholeWidth, height: wholeHeight };
  }

  const widthIsLong = wholeWidth >= wholeHeight;
  let newShortest: number;
  let newLongest: number;
  if (limit.shortMm * longest <= limit.longMm * shortest) {
    // The short side binds.
    newShortest = limit.shortMm;
    newLongest = divideRoundHalfUp(longest * limit.shortMm, shortest);
  } else {
    // The long side binds.
    newLongest = limit.longMm;
    newShortest = divideRoundHalfUp(shortest * limit.longMm, longest);
  }
  newShortest = Math.min(Math.max(newShortest, 1), limit.shortMm);
  newLongest = Math.min(Math.max(newLongest, 1), limit.longMm);

  return widthIsLong
    ? { width: newLongest, height: newShortest }
    : { width: newShortest, height: newLongest };
}

// The API's own range per side (LogoMethodMaxSizePolicy.MinMm / MaxMm); the
// admin box passes the range the API reports, these are only the fallback
// for an answer without it. The floor is 100 mm on purpose: a size typed in
// centimetres ("29x40" for 290x400) must be refused, not stored.
export const MAX_SIZE_INPUT_MIN_MM = 100;
export const MAX_SIZE_INPUT_MAX_MM = 2000;

/**
 * Parses the admin "Max size (mm)" field. Empty (or only whitespace) is
 * valid and means "no override" (normalized ""). Otherwise the text must be
 * "WIDTHxHEIGHT" in whole mm with both numbers within minMm-maxMm (default
 * 100-2000, the API's own range - the admin box passes the range the API
 * reports); normalized is "AxB" without spaces, in the order typed.
 * @param raw The text typed by the admin
 * @param minMm Smallest accepted side in mm (default 100)
 * @param maxMm Largest accepted side in mm (default 2000)
 * @returns { valid, normalized }
 */
export function parseMaxSizeInput(
  raw: string,
  minMm: number = MAX_SIZE_INPUT_MIN_MM,
  maxMm: number = MAX_SIZE_INPUT_MAX_MM
): { valid: boolean; normalized: string } {
  const text = raw ?? '';
  if (text.trim() === '') {
    return { valid: true, normalized: '' };
  }

  const match = /^\s*(\d{1,4})\s*[xX]\s*(\d{1,4})\s*$/.exec(text);
  if (!match) {
    return { valid: false, normalized: '' };
  }

  const a = Number(match[1]);
  const b = Number(match[2]);
  const inRange = (n: number) => n >= minMm && n <= maxMm;
  if (!inRange(a) || !inRange(b)) {
    return { valid: false, normalized: '' };
  }

  return { valid: true, normalized: `${a}x${b}` };
}
